import React from 'react';
import { createTwoFilesPatch } from 'diff';
import { html } from 'diff2html';
import { FormattedMessage } from 'react-intl';
import './DiffViewer.scss';

export const DEFAULT_DIFF_MAX_CHARS = 200000;
export const DEFAULT_DIFF_MAX_LINES = 4000;
const TRUNCATED_DIFF_MARKER = '... diff content truncated ...';

interface DiffViewerProps {
    expected: string;
    actual: string;
    filename?: string;
    maxChars?: number;
    maxLines?: number;
}

interface DiffViewerState {
    fullHtml: string | null;
    isTruncated: boolean;
}

interface DiffTextResult {
    text: string;
    truncated: boolean;
    originalChars: number;
    displayedChars: number;
}

class DiffViewer extends React.Component<DiffViewerProps, DiffViewerState> {
    static defaultProps = {
        filename: 'snapshot.txt',
        maxChars: DEFAULT_DIFF_MAX_CHARS,
        maxLines: DEFAULT_DIFF_MAX_LINES,
    };

    constructor(props: DiffViewerProps) {
        super(props);
        this.state = this.computeDiff(props);
    }

    componentWillReceiveProps(nextProps: DiffViewerProps) {
        if (
            this.props.expected !== nextProps.expected ||
            this.props.actual !== nextProps.actual ||
            this.props.maxChars !== nextProps.maxChars ||
            this.props.maxLines !== nextProps.maxLines
        ) {
            this.setState(this.computeDiff(nextProps));
        }
    }

    truncateMiddleByChars(source: string, maxChars: number): string {
        if (maxChars <= 0 || source.length <= maxChars) {
            return source;
        }

        const marker = `\n${TRUNCATED_DIFF_MARKER}\n`;
        if (maxChars <= marker.length + 2) {
            return source.slice(0, maxChars);
        }

        const keepChars = maxChars - marker.length;
        const headChars = Math.ceil(keepChars / 2);
        const tailChars = Math.floor(keepChars / 2);
        return `${source.slice(0, headChars)}${marker}${source.slice(source.length - tailChars)}`;
    }

    truncateMiddleByLines(source: string, maxLines: number): string {
        if (maxLines <= 0) {
            return source;
        }

        const lines = source.split('\n');
        if (lines.length <= maxLines) {
            return source;
        }

        if (maxLines === 1) {
            return TRUNCATED_DIFF_MARKER;
        }

        const keepLines = maxLines - 1;
        const headLines = Math.ceil(keepLines / 2);
        const tailLines = Math.floor(keepLines / 2);
        return [
            ...lines.slice(0, headLines),
            TRUNCATED_DIFF_MARKER,
            ...lines.slice(lines.length - tailLines),
        ].join('\n');
    }

    truncateDiffText(value: string, props: DiffViewerProps): DiffTextResult {
        const maxChars = props.maxChars || DEFAULT_DIFF_MAX_CHARS;
        const maxLines = props.maxLines || DEFAULT_DIFF_MAX_LINES;
        const source = value || '';
        let text = source;
        let truncated = false;

        if (text.length > maxChars) {
            text = this.truncateMiddleByChars(text, maxChars);
            truncated = true;
        }

        const lineTruncatedText = this.truncateMiddleByLines(text, maxLines);
        if (lineTruncatedText !== text) {
            text = lineTruncatedText;
            truncated = true;
        }

        return {
            text,
            truncated,
            originalChars: source.length,
            displayedChars: text.length,
        };
    }

    computeDiff(props: DiffViewerProps): DiffViewerState {
        const { filename } = props;
        const expected = this.truncateDiffText(props.expected, props);
        const actual = this.truncateDiffText(props.actual, props);

        const patch = createTwoFilesPatch(filename || 'snapshot.txt', filename || 'snapshot.txt', expected.text, actual.text, 'Expected', 'Actual');

        let diffHtml = html(patch, {
            drawFileList: false,
            matching: "lines",
            outputFormat: "side-by-side",
            renderNothingWhenEmpty: false
        });

        const masks = ['id', 'timestamp', 'secret', 'count', 'version', 'session', 'url', 'string'];
        masks.forEach(mask => {
            const pattern = `\\{${mask}\\}`;
            const regex = new RegExp(pattern, 'g');
            diffHtml = diffHtml.replace(regex, `<span class="masked-field" title="This field is masked and ignored during comparison">{${mask}}</span>`);
        });

        return {
            fullHtml: diffHtml,
            isTruncated: expected.truncated || actual.truncated,
        };
    }

    render() {
        const { fullHtml, isTruncated } = this.state;

        return (
            <div className="diff-viewer">
                {isTruncated && (
                    <div className="diff-viewer__truncated-alert">
                        <FormattedMessage
                            defaultMessage="Large diff content was truncated before rendering."
                            id="snapshotTesting.diffTruncated"
                        />
                    </div>
                )}
                {fullHtml && <div dangerouslySetInnerHTML={{ __html: fullHtml }} />}
            </div>
        );
    }
}

export default DiffViewer;
